/* eslint-disable */
// Only functions used in the app are implemented.

export function debounce(fn, wait = 200) {
  let timeoutId;
  const debounced = function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), wait);
  };
  debounced.cancel = () => clearTimeout(timeoutId);
  return debounced;
}

export function throttle(fn, wait = 200) {
  let lastCallTime = 0;
  let timeoutId = null;

  const throttled = function (...args) {
    const now = Date.now();
    const timeSinceLastCall = now - lastCallTime;

    if (timeSinceLastCall >= wait) {
      lastCallTime = now;
      fn.apply(this, args);
    } else {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        lastCallTime = Date.now();
        fn.apply(this, args);
        timeoutId = null;
      }, wait - timeSinceLastCall);
    }
  };

  throttled.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return throttled;
}

export function formatTime(totalSeconds = 0) {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (!parts.length) parts.push(`${secs}s`);
  return parts.join(' ');
}

export function convertSecondsToTimeUnit(totalSeconds = 0) {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h, m, s].map(v => String(v).padStart(2, '0')).join(':');
}

function parseRGB(background) {
  if (typeof background !== 'string') return null;
  const s = background.trim();
  // #rgb / #rrggbb
  if (s[0] === '#') {
    const hex = s.slice(1);
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      if ([r, g, b].every(v => Number.isFinite(v))) return [r, g, b];
    } else if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      if ([r, g, b].every(v => Number.isFinite(v))) return [r, g, b];
    }
    return null;
  }
  // rgb(a)
  const m = s.match(/rgba?\(([^)]+)\)/i);
  if (m) {
    const parts = m[1].split(',').map(v => Number(v.trim()));
    const [r, g, b] = parts;
    if ([r, g, b].every(v => Number.isFinite(v))) return [r, g, b];
    return null;
  }
  return null;
}

export function getContrastingTextColor(background = '#ffffff') {
  const rgb = parseRGB(background) || [255, 255, 255];
  const [rs, gs, bs] = rgb.map(v => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  const luminance = 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  return luminance > 0.5 ? '#1f2937' : '#ffffff';
}

export function splitName(fullName = '') {
  const name = String(fullName).trim().replace(/\s+/g, ' ');
  if (!name) return { firstName: '', lastName: '' };
  const parts = name.split(' ');
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts.slice(-1)[0],
  };
}

export function fileNameWithEllipsis(fileName = '', maxLength = 30) {
  const name = String(fileName);
  if (name.length <= maxLength) return name;
  const keep = Math.max(3, Math.floor((maxLength - 3) / 2));
  return `${name.slice(0, keep)}...${name.slice(-keep)}`;
}

export function trimContent(content = '') {
  return String(content || '').trim();
}

export function parseBoolean(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'y', 'on'].includes(v)) return true;
    if (['false', '0', 'no', 'n', 'off', ''].includes(v)) return false;
  }
  return false;
}

export function getQuantileIntervals(values = [], buckets = 5) {
  const arr = (values || [])
    .map(Number)
    .filter(v => Number.isFinite(v))
    .sort((a, b) => a - b);
  if (!arr.length || buckets <= 1) return [];
  const intervals = [];
  for (let i = 1; i < buckets; i += 1) {
    const p = i / buckets;
    const idx = Math.min(arr.length - 1, Math.max(0, Math.floor(p * (arr.length - 1))));
    intervals.push(arr[idx]);
  }
  return intervals;
}

export function replaceVariablesInMessage(template = '', variables = {}) {
  return String(template).replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_m, key) => {
    const val = variables && Object.prototype.hasOwnProperty.call(variables, key) ? variables[key] : '';
    return val == null ? '' : String(val);
  });
}

// Support object-style call: replaceVariablesInMessage({ message, variables })
export function replaceVariablesInMessage$object({ message = '', variables = {} } = {}) {
  return replaceVariablesInMessage(message, variables);
}

export function getMessageVariables({ conversation = {}, contact = {} } = {}) {
  const convo = conversation || {};
  const cnt = contact || {};
  const contactName = cnt.name || cnt.display_name || '';
  const contactEmail = cnt.email || '';
  const contactPhone = cnt.phone_number || cnt.phone || '';
  const assignee = (convo.meta && convo.meta.assignee) || {};
  const inbox = (convo.meta && convo.meta.inbox) || {};
  const account = (convo.meta && convo.meta.account) || {};
  return {
    'contact.name': contactName,
    'contact.email': contactEmail,
    'contact.phone': contactPhone,
    'conversation.id': convo.id || '',
    'conversation.display_id': convo.display_id || '',
    'assignee.name': assignee.name || '',
    'assignee.email': assignee.email || '',
    'inbox.name': inbox.name || '',
    'account.name': account.name || '',
  };
}

export function getUndefinedVariablesInMessage({ message = '', variables = {} } = {}) {
  const found = new Set();
  const missing = new Set();
  String(message).replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_m, key) => {
    found.add(key);
    if (!Object.prototype.hasOwnProperty.call(variables, key) || variables[key] == null || variables[key] === '') {
      missing.add(key);
    }
    return '';
  });
  return Array.from(missing);
}

// Placeholders for complex helpers; implement when needed
export function evaluateSLAStatus() {
  // Implement business-specific logic if required by UI; returning 'on_track' as default
  return 'on_track';
}

export function createTypingIndicator(onStart = () => {}, onStop = () => {}, idleMs = 4000) {
  let timeoutId = null;
  let active = false;

  const scheduleStop = () => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      if (active) {
        active = false;
        try {
          onStop();
        } catch (e) {}
      }
    }, idleMs);
  };

  return {
    start() {
      if (!active) {
        active = true;
        try {
          onStart();
        } catch (e) {}
      }
      scheduleStop();
    },
    stop() {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (active) {
        active = false;
        try {
          onStop();
        } catch (e) {}
      }
    },
    dispose() {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = null;
      active = false;
    },
  };
}

export default {
  debounce,
  throttle,
  formatTime,
  convertSecondsToTimeUnit,
  getContrastingTextColor,
  splitName,
  fileNameWithEllipsis,
  trimContent,
  getQuantileIntervals,
  replaceVariablesInMessage,
  replaceVariablesInMessage$object,
  evaluateSLAStatus,
  createTypingIndicator,
  parseBoolean,
  getMessageVariables,
  getUndefinedVariablesInMessage,
};
